import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';

import { Personas } from '@shared/models/personas.model';
import { urlEndPoints } from '../config/end-points';
import { User } from '../models/user.model';

@Injectable({
  providedIn: 'root',
})
export class UserDal {
  constructor(private readonly httpClient: HttpClient) {}

  getAllUsers(options?: any): Observable<User[]> {
    let commonParams;
    if (options) {
      const sortArgs = options.sort ? this.formatSortObject(options.sort) : null;
      const searchArgs = options.search ? this.formatSearchObject(options.search) : null;
      commonParams =
        'page=' + (options.page || 1) + '&limit=' + (options.limit || 10) + (sortArgs || '') + (searchArgs || '');
    }
    const endPoint = urlEndPoints.users + (commonParams ? '?' + commonParams : '');
    return this.httpClient.get<User[]>(endPoint);
  }

  formatSortObject(sort: { [key: string]: string }): string {
    let sortString = '';
    for (const [key, value] of Object.entries(sort)) {
      if ('string' === typeof value && '' !== value.trim()) {
        sortString += `&sort[${key}]=${value}`;
      }
    }
    return sortString;
  }

  formatSearchObject(search: { [key: string]: string }): string {
    let searchString = '';
    for (const [key, value] of Object.entries(search)) {
      if ('string' === typeof value && '' !== value.trim()) {
        searchString += `&search[${key}]=${value}`;
      }
    }
    return searchString;
  }

  addUser(user: User): Observable<any> {
    return this.httpClient.post<any>(urlEndPoints.users, user);
  }

  addUsers(users: User[]): Observable<any> {
    return this.httpClient.post<any>(urlEndPoints.users + 'addUsers', users);
  }

  modifyUser(user: User): Observable<User> {
    return this.httpClient.put<User>(urlEndPoints.users + user._id, user);
  }

  modifyUserMail(userId: string, email: string): Observable<User> {
    return this.httpClient.put<User>(urlEndPoints.users + userId, { email: email });
  }

  archiveUser(idUser: string): Observable<User> {
    return this.httpClient.delete<User>(urlEndPoints.users + idUser);
  }

  getUser(idUser: string): Observable<any> {
    return this.httpClient.get<User>(urlEndPoints.users + idUser);
  }

  getPersonas(): Observable<Personas[]> {
    return this.httpClient.get<Personas[]>(urlEndPoints.users + 'personas');
  }

  getUserByProfileId(profileId: string): Observable<any> {
    return this.httpClient.get<User>(urlEndPoints.users + 'by-profile/' + profileId);
  }

  getUsersCounter(): Observable<number> {
    return this.httpClient.get<number>(urlEndPoints.users + 'counter/');
  }

  checkAccessOptions(uniqueHash: string): Observable<any> {
    return this.httpClient.get<any>(urlEndPoints.users + 'checkAccessOptions/' + uniqueHash);
  }

  hardDeleteUser(user: User): Observable<User> {
    return this.httpClient.delete<User>(urlEndPoints.users + 'hardDelete/' + user._id);
  }

  verifyOtp(code: string, twoFactorCookie: string): Observable<any> {
    return this.httpClient.post<any>(urlEndPoints.users + 'verify-otp', { otp: code, twoFactorCookie });
  }

  resendOtp(twoFactorCookie: string): Observable<any> {
    return this.httpClient.post<any>(urlEndPoints.users + 'send-otp', { twoFactorCookie });
  }

  validatePassword(password: string): Observable<{ valid: boolean }> {
    return this.httpClient.post<{ valid: boolean }>(urlEndPoints.validatePassword, { password });
  }
}
